import React from "react";
import MetricCard from "./MetricCard";
import { formatUSD } from "../../utils/formatters";
import type { MetricSample } from "../../hooks/useMetricSamples";

export interface SalesPerformanceKpis {
  aov: number;
  aovChange?: number;
  dealsClosed: number;
  dealsChange?: number;
  closeRate: number;
  closeRateChange?: number;
  showUpRate: number;
  showUpRateChange?: number;
  /** Both sub-slices share showUpRate's denominator: the three sum to 100. */
  noShowRate?: number;
  orphanShareRate?: number;
  orphanRate: number;
  cancellations: number;
  shows: number;
  noShows: number;
  scoNoShows: number;
  orphanPreCalls: number;
  cashPerCallTaken: number;
  cashCollected: number;
  newDMChats: number;
  deepConversations: number;
  setterBookedCalls: number;
}

interface SalesPerformanceCardProps {
  kpis: SalesPerformanceKpis;
  funnelMerged: {
    pre_calls_filled?: number;
    // Owner split of pre_calls_filled (setter + dialer + self = pre_calls_filled).
    pre_calls_setter_owned?: number;
    pre_calls_dialer_owned?: number;
    pre_calls_self_booked?: number;
  };
  /** Admin dashboard renders the pre-call owner split (setter/dialer/self) under the
   *  Pre-Calls tile. Portal omits it until Mako opts in (Jun 23, 2026 — admin first). */
  showPreCallOwnerSplit?: boolean;
  comparisonLabel?: string | null;
  /** Optional. When omitted, MetricInfoIcon is not rendered for any tile.
   *  Portal omits this because `usePortalMetricSamples` lacks the Sales Performance keys.
   *  Dashboard passes its full `getSamples` from `useOperationsAnalytics`. */
  getSamples?: (key: string) => MetricSample[];
  /** HP-A: when provided, the Show-up Rate tile becomes clickable and opens the
   *  breakdown modal (ShowRateBreakdownModal). Optional so portal/self-set call
   *  sites that don't wire the modal are unaffected. */
  onShowUpRateClick?: () => void;
}

const SalesPerformanceCard: React.FC<SalesPerformanceCardProps> = ({
  kpis,
  funnelMerged,
  showPreCallOwnerSplit,
  comparisonLabel,
  getSamples,
  onShowUpRateClick,
}) => (
  <div className="liquid-glass anim-lift p-4 space-y-5 h-full">
    {/* Sales & Conversion — IDENTICAL to OperationsDashboard:381-428.
        NOTE: span for comparisonLabel is rendered unconditionally to match dashboard
        byte-for-byte. React renders {null} or {undefined} as nothing inside the span,
        but the span DOM node is preserved — important for pixel regression test. */}
    <div>
      <h3 className="token-14s mb-3 flex items-center gap-2">
        <span className="section-bar" />
        Sales & Conversion
        <span className="token-10 text-glass-muted font-normal normal-case tracking-normal ml-1">
          {comparisonLabel}
        </span>
      </h3>
      {/* S51 (May 15 PM): 5 tiles → 4. Orphan Rate collapsed into Show-up Rate's
          sub-line — they share a denominator, the relationship is tighter as a
          dependent fact than a peer tile. Eliminates the lonely-cell row 3 from
          the prior 2-col-of-5 layout. */}
      <div className="grid grid-cols-2 gap-3">
        <MetricCard
          label="Avg Cash per Close"
          value={formatUSD(kpis.aov)}
          change={kpis.aovChange}
          changeFormat="dollar"
          metricKey="aov"
          getSamples={getSamples}
          hero
        />
        <MetricCard
          label="Deals Closed"
          value={kpis.dealsClosed.toString()}
          change={kpis.dealsChange}
          changeFormat="number"
          metricKey="dealsClosed"
          getSamples={getSamples}
        />
        <MetricCard
          label="Close Rate"
          value={`${kpis.closeRate.toFixed(1)}%`}
          change={kpis.closeRateChange}
          metricKey="closeRate"
          getSamples={getSamples}
        />
        <MetricCard
          label="Show-up Rate"
          value={`${kpis.showUpRate.toFixed(1)}%`}
          change={kpis.showUpRateChange}
          metricKey="showUpRate"
          getSamples={getSamples}
          sub={
            kpis.noShowRate !== undefined
              ? `No-show: ${kpis.noShowRate.toFixed(1)}% · Orphan: ${(kpis.orphanShareRate ?? kpis.orphanRate).toFixed(1)}%`
              : `Orphan: ${kpis.orphanRate.toFixed(1)}%`
          }
          onClick={onShowUpRateClick}
        />
      </div>
    </div>

    {/* Divider */}
    <div className="border-t border-black/[0.06]" />

    {/* S51 (May 15 PM): "Call Summary" 6-tile cramped row split into two named
        sections — Call Pipeline (lead's funnel-stage counts, left→right journey)
        and Cash Yield (the dollar-per-stage ratios). Pipeline reads like the
        lead's actual trajectory: pre-call → cancel → show → no-show. Yield is
        the money question, separated so dollar amounts don't compete for column
        width with count tiles. */}
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="token-14s mb-3 flex items-center gap-2">
          <span className="section-bar" />
          Call Pipeline
          <span className="token-10 text-glass-muted font-normal normal-case tracking-normal ml-1">
            lead's journey →
          </span>
        </h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <div>
            <div className="token-10u text-glass-muted">Pre-Calls</div>
            <div className="token-15 text-glass">
              {(funnelMerged.pre_calls_filled ?? 0).toLocaleString()}
            </div>
            {showPreCallOwnerSplit && (funnelMerged.pre_calls_filled ?? 0) > 0 && (
              <div className="token-9 text-glass-muted">
                {(funnelMerged.pre_calls_setter_owned ?? 0).toLocaleString()} setter ·{" "}
                {(funnelMerged.pre_calls_dialer_owned ?? 0).toLocaleString()} dialer ·{" "}
                {(funnelMerged.pre_calls_self_booked ?? 0).toLocaleString()} self
              </div>
            )}
          </div>
          <div>
            <div className="token-10u text-glass-muted">Cancellations</div>
            <div className="token-15 text-glass">{kpis.cancellations.toLocaleString()}</div>
          </div>
          <div>
            <div className="token-10u text-glass-muted">Shows</div>
            <div className="token-15 text-glass">{kpis.shows.toLocaleString()}</div>
            {kpis.shows > 0 && (
              <div className="token-9 text-glass-muted">{kpis.showUpRate.toFixed(0)}% show-up</div>
            )}
          </div>
          <div>
            <div className="token-10u text-glass-muted">No-Shows</div>
            <div className="token-15 text-glass">{kpis.noShows.toLocaleString()}</div>
            {kpis.noShows > 0 && (
              <div className="token-9 text-glass-muted">
                {kpis.scoNoShows} call · {kpis.orphanPreCalls} orphan
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <h3 className="token-14s mb-3 flex items-center gap-2">
          <span className="section-bar" />
          Cash Yield
        </h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <div className="token-10u text-glass-muted">Cash / Show</div>
            <div className="token-15 text-glass">{formatUSD(kpis.cashPerCallTaken)}</div>
          </div>
          <div>
            <div className="token-10u text-glass-muted">Cash / Pre-Call</div>
            <div className="token-15 text-glass">
              {formatUSD(
                funnelMerged.pre_calls_filled && funnelMerged.pre_calls_filled > 0
                  ? kpis.cashCollected / funnelMerged.pre_calls_filled
                  : 0
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="token-14s mb-3 flex items-center gap-2">
          <span className="section-bar" />
          DM Health
        </h3>
        <div className="grid grid-cols-3 gap-x-6 gap-y-4">
          <div>
            <div className="token-10u text-glass-muted">New Chats</div>
            <div className="token-15 text-glass">{kpis.newDMChats.toLocaleString()}</div>
          </div>
          <div>
            <div className="token-10u text-glass-muted">Conversations</div>
            <div className="token-15 text-glass">{kpis.deepConversations.toLocaleString()}</div>
            {kpis.newDMChats > 0 && (
              <div className="token-9 text-glass-muted">
                {((kpis.deepConversations / kpis.newDMChats) * 100).toFixed(0)}% conversion
              </div>
            )}
          </div>
          <div>
            <div className="token-10u text-glass-muted">Setter Booked</div>
            <div className="token-15 text-glass">{kpis.setterBookedCalls.toLocaleString()}</div>
            {kpis.deepConversations > 0 && (
              <div className="token-9 text-glass-muted">
                {((kpis.setterBookedCalls / kpis.deepConversations) * 100).toFixed(0)}% of convos
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default SalesPerformanceCard;
